import {accountPaths, ordersPaths} from '~/utils/services/paths';
import {$cpFetch} from '~/composables/useCpFetch';
import {useOrdersStore, useCheckoutStore} from '~/stores';
import {useCpUser} from '~/composables/useCpUser';
import type {
    IPaginatedResponse,
    IOrder,
    ITrackingCode,
    IPaymentBlock,
    IArticleOrder,
    IReportSubtype,
    IReportType,
    ISingleOrderReturnCases,
    IArticlesPerWaybill,
    IPaymentInstructions,
} from '~/types';
import {getDeliveryAddressMD5} from '~/utils/services/api/account/addresses/addresses';
import {getEnv} from '~/utils/services/api';

type IGeneralResponse = {
    status: 'ok' | 'error'
    msg: string
}

export type ICancelOrderResponse = {
    status: 'ok' | 'error',
    msg: string,
    code: number,
    title: string
}

export type ICreateOrderResponse = {
    status: string
    code: number
    msg: string
    orderId: string
}

export type IOverdueArticles = {
    data: Array<IArticleOrder>
}

export type ICreateReturnCase = {
    type: IReportSubtype | IReportType | undefined,
    typeNeedPhotos: number,
    article: string,
    amount: number,
    description?: string,
    componentsConnected?: string,
    originalPackaging: boolean | number,
    nrOpenArticles: number,
    preferredSolution: 'refund' | 'replacement',
    serialNumber?: string,
    photos: photoSetType,
    folio?: string,
}

export type IUpdateReturnCase = {
    article: string,
    serialNumber?: string,
    amount: number,
    description: string,
    preferredSolution?: 'refund' | 'replacement',
    photos?: Array<{
        name: string,
        type: string,
        file: any,
    }>
}

export type IProportionalDeliveryCostResponse = {
    data: {
        deliverycost: number
    }
}

export const getOrders = (page = 1) => {
    const ordersStoreFilters = useOrdersStore().getOrdersStore;
    const filters: any = {};
    if (ordersStoreFilters.currentSearch) {
 filters.search = ordersStoreFilters.currentSearch;
}
    if (ordersStoreFilters.timeInterval) {
        if (ordersStoreFilters.timeInterval === 'archived') {
            filters.time = 1;
            filters.archived = true;
        } else {
            filters.time = ordersStoreFilters.timeInterval;
        }
    }
    return $cpFetch<IPaginatedResponse<IOrder>>(accountPaths.orders(useCpUser().userEmail!), {
        params: {...filters, 'page[number]': page},
    });
};

export const sendOrdersConfirmationMail = (email: string) => {
    return $cpFetch<IGeneralResponse>(accountPaths.ordersConfirmationMail(useCpUser().userEmail!, useOrdersStore().getSelectedOrderId!), {
        method: 'POST',
        body: {
            email,
        },
    });
};

export const getOrdersPreviewMail = () => {
    return $cpFetch<HTMLElement>(accountPaths.ordersPreviewMail(useCpUser().userEmail!, useOrdersStore().getSelectedOrderId!));
};

export const cancelOrder = () => {
    return $cpFetch<ICancelOrderResponse>(accountPaths.cancelOrders(useCpUser().userEmail!, useOrdersStore().getSelectedOrderId!), {
        method: 'DELETE',
    });
};

export interface ICreateOrderOptions {
    jumio_flag: 1 | 0
    paypalMetaId?: string
}
export interface CreateOrderParams {
    paypal_id?: string;
    paypal_installments?: number | string;
    paypal_metaId?: string;
    /** Hosted Fields or Remembered Cards */
    paypal_type?: 'hf' | 'rc';
}

export const createOrder = async (paymentMethod: string | null | undefined, options: ICreateOrderOptions = {jumio_flag: 0}) => {
    // Call env
    const {sessionToken} = await getEnv();
    const {deliveryAddressMD5} = await getDeliveryAddressMD5(useCpUser().userEmail ?? '');
    let body: CreateOrderParams = {};
    if (paymentMethod === 'cppaypalbilling') {
        body = {
            paypal_installments: Number(useCheckoutStore().month),
            paypal_metaId: options.paypalMetaId,
        };
    }
    if (paymentMethod === 'cppaypalppcp') {
        body = {
            paypal_type: useCheckoutStore().ppcp.type ?? undefined,
            paypal_id: useCheckoutStore().ppcp.id ?? undefined,
            paypal_installments: useCheckoutStore().ppcp.month ?? undefined,
            paypal_metaId: options.paypalMetaId,
        };
    }
    return $cpFetch<ICreateOrderResponse>(accountPaths.orders(useCpUser().userEmail!), {
        method: 'POST',
        body,
        params: {
            sDeliveryAddressMD5: deliveryAddressMD5,
            stoken: sessionToken,
            meta_id: options.paypalMetaId,
        },
        query: {
            jumio_flag: options.jumio_flag,
        },
    });
};

export const getOrder = (id: string) => $cpFetch<IOrder>(accountPaths.orders(useCpUser().userEmail!, id));

export const getTrackingCodes = (id: string) => $cpFetch<ITrackingCode>(accountPaths.ordersTrackingCodes(useCpUser().userEmail!, id));

export const getOrderPaymentBlock = (id: string) => $cpFetch<IPaymentBlock>(accountPaths.paymentBlock(useCpUser().userEmail!, id), {
    params: {
        widget: true,
    },
});

export const getOrderOverdueProducts = (orderId: string) => $cpFetch<IOverdueArticles>(accountPaths.ordersOverdueProducts(useCpUser().userEmail!, orderId));

export const cancelOverdueProducts = (orderId: string, articles: Array<{ id: string, amount: number }>) => $cpFetch<IGeneralResponse>(ordersPaths.cancelOverdueProducts(orderId), {
    method: 'POST',
    parseResponse: (txt) => (txt === '' ? '' : JSON.parse(txt)),
    body: {
        articles,
    },
});

export const regenerateWaybillByReturnCase = (orderId: string, returnCaseId: string) => $cpFetch<IGeneralResponse>(ordersPaths.sendWayBillByReturnCase(orderId, returnCaseId), {
    method: 'POST',
    parseResponse: (txt) => (txt === '' ? '' : JSON.parse(txt)),
});

export const regenerateWaybillByOrder = (orderId: string) => $cpFetch<IGeneralResponse>(ordersPaths.sendWayBillByOrder(orderId), {
    method: 'POST',
    parseResponse: (txt) => (txt === '' ? '' : JSON.parse(txt)),
});

export const archiveReturnCase = (orderId: string, returnCaseId: string, comments = '') => $cpFetch(ordersPaths.archiveReturnCase(orderId, returnCaseId), {
    method: 'POST',
    parseResponse: (txt) => (txt === '' ? '' : JSON.parse(txt)),
    body: {
        comments,
    },
});

export const createReturnCase = (orderId: string, payload: ICreateReturnCase) => {
    const formData = new FormData();
    Object.entries(payload).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        // Photo array
        value.forEach((photo) => formData.append('photos[]', photo.file));
      } else {
        formData.append(key, `${value}`);
      }
    });
    return $cpFetch(ordersPaths.createReturnCase(orderId), {
        method: 'POST',
        parseResponse: (txt) => (txt === '' ? '' : JSON.parse(txt)),
        body: formData,
    });
};

export const updateReturnCase = (orderId: string, payload: IUpdateReturnCase, returnCaseId: string) => {
    const formData = new FormData();
    Object.entries(payload).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        // Photo array
        value.forEach((photo) => formData.append('photos[]', photo.file));
      } else {
        formData.append(key, `${value}`);
      }
    });
    return $cpFetch(ordersPaths.updateReturnCase(orderId, returnCaseId), {
        method: 'POST',
        parseResponse: (txt) => (txt === '' ? '' : JSON.parse(txt)),
        body: formData,
    });
};

export const getProportionalDeliveryCost = (orderId: string, articlesIds: Array<string>) => $cpFetch<IProportionalDeliveryCostResponse>(accountPaths.proportionalDeliveryCost(useCpUser().userEmail!, orderId), {
    params: {
        articles: articlesIds,
    },
});

export const getOrderReturnCases = (orderId: string, returnCaseId?: string, articleSKU?: string) => $cpFetch<ISingleOrderReturnCases>(accountPaths.returnCases(useCpUser().userEmail!, orderId), {
    params: {
        rc_id: returnCaseId,
        article: articleSKU,
    },
});

export const getArticlesPerWaybill = (orderId: string) => $cpFetch<IArticlesPerWaybill>(accountPaths.articlesPerWaybill(useCpUser().userEmail!, orderId));

export const getPaymentInstructions = (orderId: string) => $cpFetch<IPaymentInstructions>(ordersPaths.paymentInstructions(orderId));

export const setArticleAsDelivered = (orderId: string, articleId: string) => $cpFetch<IGeneralResponse>(ordersPaths.setArticleAsDelivered(orderId), {
    method: 'POST',
    parseResponse: (txt) => (txt === '' ? '' : JSON.parse(txt)),
    body: {
        article: articleId,
    },
});